import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/header';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  House,
  Car,
  FileText,
  User,
  ChartBar,
  ChatCircle,
  Briefcase,
  BookOpen,
  DownloadSimple,
  Globe,
  SignOut,
} from '@phosphor-icons/react';
import { useLanguage } from '../context/language-context';
import { useAuth } from '../context/auth-context';

// Define types for menu items
interface BaseMenuItem {
  id: string;
  titleEn: string;
  titleAr: string;
  icon: React.ComponentType<any>;
  path: string;
}

interface StandardMenuItem extends BaseMenuItem {}

interface LanguageMenuItem extends BaseMenuItem {
  onClick: () => void;
  info: string;
}

type MenuItem = StandardMenuItem | LanguageMenuItem;

// Menu items data without the language item
const staticMenuItems: StandardMenuItem[] = [
  {
    id: 'home',
    titleEn: 'Home',
    titleAr: 'الرئيسية',
    icon: House,
    path: '/',
  },
  {
    id: 'car-delivery',
    titleEn: 'Car Delivery',
    titleAr: 'تسليم السيارات',
    icon: Car,
    path: '/car-delivery',
  },
  {
    id: 'legislation',
    titleEn: 'Legislation',
    titleAr: 'التشريعات',
    icon: FileText,
    path: '/legislation',
  },
  {
    id: 'my-data',
    titleEn: 'My Data',
    titleAr: 'بياناتي',
    icon: User,
    path: '/my-data',
  },
  {
    id: 'performance',
    titleEn: 'Performance Evaluation',
    titleAr: 'تقييم الأداء',
    icon: ChartBar,
    path: '/performance',
  },
  {
    id: 'send-note',
    titleEn: 'Send Note',
    titleAr: 'إرسال ملاحظة',
    icon: ChatCircle,
    path: '/send-note',
  },
  {
    id: 'office-policy',
    titleEn: 'Office Policy',
    titleAr: 'سياسة مكاتب العمل',
    icon: Briefcase,
    path: '/office-policy',
  },
  {
    id: 'labor-market',
    titleEn: 'Labor Market Magazine',
    titleAr: 'مجلة سوق العمل',
    icon: BookOpen,
    path: '/labor-market',
  },
  {
    id: 'recruitment',
    titleEn: 'Recruitment Guide',
    titleAr: 'دليل الإستخدام',
    icon: DownloadSimple,
    path: '/recruitment',
  },
];

// Helper function to get user initials from name
const getInitials = (name: string): string => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Helper function to format date
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const MenuPage = () => {
  // Use the language context instead of local state
  const { language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const [greeting, setGreeting] = useState({ en: '', ar: '' });

  // Extract user information from the token
  const userProfile = {
    name: user?.profile?.name || 'Unknown User',
    role: user?.profile?.realm_access?.roles?.[0] || 'Inspector',
    groups: user?.profile?.groups || [],
    email: user?.profile?.email || 'No email provided',
    avatar: getInitials(user?.profile?.name || 'Unknown User'),
    lastLogin: formatDate(new Date()),
  };

  // Set greeting based on time of day
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setGreeting({ en: 'Good morning', ar: 'صباح الخير' });
    } else if (hour >= 12 && hour < 18) {
      setGreeting({ en: 'Good afternoon', ar: 'مساء الخير' });
    } else {
      setGreeting({ en: 'Good evening', ar: 'مساء الخير' });
    }
  }, []);

  // Create a complete menu items array with the language item
  const menuItems: MenuItem[] = [
    ...staticMenuItems,
    {
      id: 'language',
      titleEn: 'Language',
      titleAr: 'اللغات',
      icon: Globe,
      path: '#', // Using # as we'll handle this with onClick
      onClick: toggleLanguage,
      info: language === 'en' ? 'English' : 'العربية',
    },
  ];

  return (
    <div
      className="flex flex-col min-h-screen"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Header
        title={language === 'en' ? 'My Account' : 'حسابي'}
        isRTL={language === 'ar'}
      />

      <div className="p-4 relative top-[5rem] space-y-4">
        {/* User Profile Section */}
        <Card className="overflow-hidden border border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-col">
              <p className="text-lg mb-2">
                {language === 'en' ? greeting.en : greeting.ar},
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Avatar className="h-16 w-16 border-2 border-[#966f2e] mr-4">
                    <AvatarFallback className="bg-[#966f2e]/10 text-[#966f2e]">
                      {userProfile.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="text-xl font-medium">
                      {userProfile.name}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {userProfile.role}
                    </p>
                    <p className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Email'
                        : 'البريد الإلكتروني'}
                      : {userProfile.email}
                    </p>
                    <p className="text-xs text-gray-500">
                      {language === 'en' ? 'Groups' : 'المجموعات'}:{' '}
                      {userProfile.groups.join(', ')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {language === 'en'
                        ? 'Last login'
                        : 'آخر تسجيل دخول'}{' '}
                      {userProfile.lastLogin}
                    </p>
                  </div>
                </div>
                <button
                  className="text-red-600 font-medium flex items-center gap-2 "
                  onClick={logout}
                >
                  <SignOut className="w-5 h-5" />
                  {language === 'en' ? 'Logout' : 'تسجيل الخروج'}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Menu Items - keep the existing code */}
        <Card className="mb-6  border border-gray-200">
          <CardContent className="p-0">
            <div className="divide-y divide-gray-200">
              {menuItems.map((item) => {
                // For the language item, we want to handle the click differently
                if (
                  item.id === 'language' &&
                  'onClick' in item &&
                  'info' in item
                ) {
                  return (
                    <button
                      key={item.id}
                      className="flex items-center justify-between p-4 w-full text-left"
                      onClick={item.onClick}
                    >
                      <div className="flex items-center">
                        <item.icon
                          className={`w-5 h-5 ${
                            language === 'ar' ? 'ml-3' : 'mr-3'
                          } text-[#966f2e]`}
                        />
                        <span>
                          {language === 'en'
                            ? item.titleEn
                            : item.titleAr}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span
                          className={`text-sm text-[#966f2e] ${
                            language === 'ar' ? 'ml-2' : 'mr-2'
                          }`}
                        >
                          {item.info}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-5 h-5"
                          style={{
                            transform:
                              language === 'ar'
                                ? 'rotate(180deg)'
                                : 'none',
                          }}
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </div>
                    </button>
                  );
                }

                // For all other items, render as links
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    className="flex items-center justify-between p-4"
                  >
                    <div className="flex items-center">
                      <item.icon
                        className={`w-5 h-5 ${
                          language === 'ar' ? 'ml-3' : 'mr-3'
                        } text-[#966f2e]`}
                      />
                      <span>
                        {language === 'en'
                          ? item.titleEn
                          : item.titleAr}
                      </span>
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-5 h-5"
                      style={{
                        transform:
                          language === 'ar'
                            ? 'rotate(180deg)'
                            : 'none',
                      }}
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
        {/* Logout Link */}
        <div className="mt-6">
          <button
            className="text-red-600 font-medium flex items-center gap-2 "
            onClick={logout}
          >
            <SignOut className="w-5 h-5" />
            {language === 'en' ? 'Logout' : 'تسجيل الخروج'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuPage;
